import type { ExcalidrawElement } from "@excalidraw/element/types";

import { isSpringParams } from "./spring";
import { isValidTracks } from "./tracks";
import { animationPresets } from "./presets/registry";

import type { ClipTracks } from "./tracks";

import type { SpringParams } from "./spring";

/** Animation duration constraints (ms). UI enforces [100, 60000], runtime guards to >= 1 —
 * validator now enforces the documented [100, 60000] bounds. */
export const MIN_ANIMATION_DURATION_MS = 100;
export const MAX_ANIMATION_DURATION_MS = 60000;

/** Animation delay constraints (ms). */
export const MIN_ANIMATION_DELAY_MS = 0;
export const MAX_ANIMATION_DELAY_MS = 60000;

/** Persisted on `element.customData.animation` — serializes with the scene,
 * participates in undo/redo and copy/paste. */
export type ElementAnimation = {
  /** preset registry key, e.g. "rotate" */
  preset: string;
  /** ms */
  duration: number;
  /** ms */
  delay: number;
  /** extra loops; 0 = play once, -1 = infinite (preview only — export
   * compiles -1 as a single cycle so the timeline duration stays finite) */
  repeat: number;
  /** reverse on alternate cycles (only meaningful with repeat > 0) */
  yoyo?: boolean;
  /** ms gap between repeat cycles */
  repeatDelayMs?: number;
  /** ms of padding after the clip ends — pushes "after" clips later and
   * extends the timeline (breathing room in loops) */
  endDelayMs?: number;
  /** GSAP ease name, e.g. "power1.inOut". The runtime may substitute an ease
   * function when a spring is active — persisted values are always strings. */
  easing: string | ((progress: number) => number);
  params: Record<string, number>;
  /** physics-based easing (anime.js-style); when set it overrides `easing`
   * and `duration` — the spring's settling time defines the duration */
  spring?: SpringParams;
  /** id of a line/arrow/freedraw element to use as a motion path
   * (followPath preset); falls back to the nearest linear element */
  pathElementId?: string;
  /** raw SVG path data ("M0 0 L100 0 …") — morph presets sample it into a
   * custom morph target outline, overriding the built-in `shape` param */
  customPath?: string;
  /** deterministic per-element randomization: every continuous numeric param
   * plus duration/delay is scaled by up to ±30% × jitter, derived from
   * hash(seed, elementId, key) — same seed always compiles the same
   * (export-safe). jitter 0..1; enum/toggle/direction params are untouched. */
  variation?: { seed: number; jitter: number };
  /** per-channel keyframes (the `keyframed` preset compiles these);
   * t normalized 0..1 over duration, v in UI units — see animation/tracks.ts */
  tracks?: ClipTracks;
  /** how this clip starts relative to the previous clip in the element's list.
   * "after" (default) = starts when the previous clip ends;
   * "with" = starts at the same time as the previous clip (parallel). */
  startMode?: "after" | "with";
};

/** Validates that preset ID exists in the animation registry. */
const validatePresetExists = (presetId: string): boolean => {
  if (!(presetId in animationPresets)) {
    console.warn(`[animation] unknown preset "${presetId}"`);
    return false;
  }
  return true;
};

/** Validates that all preset params fall within their schema min/max bounds. */
const validateParamBounds = (
  presetId: string,
  params: Record<string, number>,
): boolean => {
  const preset = animationPresets[presetId];
  if (!preset) {
    return false; // preset not found
  }

  for (const schema of preset.paramsSchema) {
    const value = params[schema.key];
    if (value !== undefined) {
      // param is defined, check bounds
      if (value < schema.min || value > schema.max) {
        console.warn(
          `[animation] param "${schema.key}" = ${value} outside bounds [${schema.min}, ${schema.max}]`,
        );
        return false;
      }
    }
  }
  return true;
};

export const isElementAnimation = (
  value: unknown,
): value is ElementAnimation => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const v = value as Record<string, unknown>;
  const duration = v.duration as number;
  const delay = v.delay as number;
  const preset = v.preset as string;
  return (
    typeof preset === "string" &&
    validatePresetExists(preset) &&
    Number.isFinite(duration) &&
    duration >= MIN_ANIMATION_DURATION_MS &&
    duration <= MAX_ANIMATION_DURATION_MS &&
    Number.isFinite(delay) &&
    delay >= MIN_ANIMATION_DELAY_MS &&
    delay <= MAX_ANIMATION_DELAY_MS &&
    Number.isFinite(v.repeat) &&
    (v.yoyo === undefined || typeof v.yoyo === "boolean") &&
    (v.repeatDelayMs === undefined ||
      (Number.isFinite(v.repeatDelayMs) &&
        (v.repeatDelayMs as number) >= 0 &&
        (v.repeatDelayMs as number) <= MAX_ANIMATION_DELAY_MS)) &&
    (v.endDelayMs === undefined ||
      (Number.isFinite(v.endDelayMs) &&
        (v.endDelayMs as number) >= 0 &&
        (v.endDelayMs as number) <= MAX_ANIMATION_DELAY_MS)) &&
    typeof v.easing === "string" &&
    (v.spring === undefined || isSpringParams(v.spring)) &&
    (v.variation === undefined ||
      (typeof v.variation === "object" &&
        v.variation !== null &&
        Number.isFinite((v.variation as Record<string, unknown>).seed) &&
        Number.isFinite((v.variation as Record<string, unknown>).jitter) &&
        ((v.variation as Record<string, number>).jitter as number) >= 0 &&
        ((v.variation as Record<string, number>).jitter as number) <= 1)) &&
    (v.tracks === undefined || isValidTracks(v.tracks)) &&
    (v.pathElementId === undefined || typeof v.pathElementId === "string") &&
    (v.customPath === undefined || typeof v.customPath === "string") &&
    (v.startMode === undefined ||
      v.startMode === "after" ||
      v.startMode === "with") &&
    typeof v.params === "object" &&
    v.params !== null &&
    !Array.isArray(v.params) &&
    Object.values(v.params).every(
      (paramValue) =>
        typeof paramValue === "number" && Number.isFinite(paramValue),
    ) &&
    validateParamBounds(preset, v.params as Record<string, number>)
  );
};

// Screen duration lives in @excalidraw/common (shared with the in-editor
// properties panel); re-exported here so existing callers keep their import.
export {
  DEFAULT_SCREEN_DURATION_MS,
  getScreenDurationMs,
} from "@excalidraw/common";

/** Presets removed in the 2026-08 templates cleanup → the surviving preset
 * each one duplicated (or was a trivial stack of). Saved scenes keep playing:
 * ids are rewritten at the read boundary below, unknown legacy params are
 * ignored by validation, and the target's defaults fill the rest. */
export const LEGACY_PRESET_ALIASES: Record<string, string> = {
  paperWriting: "charsTypewriter",
  paperUnfold: "shapeMorph",
  timerCountdown: "pulse",
  newspaperHighlight: "charsFadeIn",
  highlightPulse: "pulse",
  emphasizeScale: "pulse",
  buildUpReveal: "zoomIn",
  revealSequence: "wipeIn",
  transitionFlow: "slideIn",
  morphPresent: "shapeMorph",
  focusSpotlight: "fade",
};

const resolveLegacyPreset = (clip: unknown): unknown => {
  if (typeof clip === "object" && clip !== null && !Array.isArray(clip)) {
    const preset = (clip as { preset?: unknown }).preset;
    if (typeof preset === "string" && LEGACY_PRESET_ALIASES[preset]) {
      return { ...clip, preset: LEGACY_PRESET_ALIASES[preset] };
    }
  }
  return clip;
};

/** Ordered clip list for an element. Reads the new `customData.animations`
 * array if present, else wraps the legacy singular `customData.animation` in a
 * one-element array, else returns []. Malformed clips are dropped (with a warn). */
export const getElementAnimations = (
  element: ExcalidrawElement,
): ElementAnimation[] => {
  const list = element.customData?.animations;
  if (Array.isArray(list)) {
    return list.map(resolveLegacyPreset).filter((clip) => {
      if (isElementAnimation(clip)) {
        return true;
      }
      console.warn(
        `[animation] skipping malformed animation clip on element ${element.id}`,
      );
      return false;
    }) as ElementAnimation[];
  }
  const legacy = getElementAnimation(element);
  return legacy ? [legacy] : [];
};

export const getElementAnimation = (
  element: ExcalidrawElement,
): ElementAnimation | null => {
  // new array wins when present
  const list = element.customData?.animations;
  if (Array.isArray(list)) {
    const first = list
      .map(resolveLegacyPreset)
      .find((clip) => isElementAnimation(clip));
    return (first as ElementAnimation) ?? null;
  }
  const animation = resolveLegacyPreset(element.customData?.animation);
  if (animation === undefined) {
    return null;
  }
  if (!isElementAnimation(animation)) {
    console.warn(
      `[animation] skipping malformed animation config on element ${element.id}`,
    );
    return null;
  }
  return animation;
};

/** Cross-ELEMENT sequencing (PowerPoint-style), stored on the element root —
 * not inside clips — so `isElementAnimation` is unaffected. Read-side coercion
 * only: garbage falls back to defaults (same pattern as getScreenDurationMs). */
export type ElementStartMode = "with" | "after";

/** "with" (default) = starts alongside the previous element in the sequence
 * (today's everyone-at-t=0 behavior); "after" = starts when the previous
 * element's clip chain ends. Note the default is inverted vs. clip-level
 * startMode. */
export const getElementStartMode = (
  element: ExcalidrawElement,
): ElementStartMode =>
  element.customData?.animationStartMode === "after" ? "after" : "with";

/** Sequence sort key; absent = end of sequence, ties broken by scene order
 * (stable sort). */
export const getElementAnimationOrder = (element: ExcalidrawElement): number =>
  Number.isFinite(element.customData?.animationOrder)
    ? (element.customData!.animationOrder as number)
    : Number.MAX_SAFE_INTEGER;

/** Builds the `customData` patch that persists a clip list on an element:
 * writes `animations` and strips the legacy singular `animation` key so the two
 * never coexist. Passing an empty list removes animation entirely. */
export const withElementAnimations = (
  element: ExcalidrawElement,
  animations: ElementAnimation[],
): Record<string, any> => {
  const { animation: _legacy, ...rest } = element.customData ?? {};
  if (animations.length === 0) {
    return rest;
  }
  return { ...rest, animations };
};
